/**
 * 알림톡 사용료 청구 (대표 전용).
 *
 * 매월 1일 09:10 스케줄러가 지난달 청구서를 자동 생성한다. 이 화면은 그 결과를 보고
 * 매장에 보낼 안내문을 복사하고, 입금을 확인 처리하는 곳이다.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  hqAlimtalkBillService,
  AlimtalkBill,
  AlimtalkBillList,
} from '../services/hqAlimtalkBillService';
// 이름표는 매장 화면·거래명세서와 같은 것을 쓴다 (한 곳만 고친다)
import { alimtalkTypeLabel as typeLabel } from '../../../shared/constants/alimtalkTypeLabels';

const won = (n: number) => (n || 0).toLocaleString('ko-KR') + '원';
const cnt = (n: number) => (n || 0).toLocaleString('ko-KR');



const prevYearMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const thisYearMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// 입금 계좌는 매장 안내문에만 쓰인다. 서버에 둘 만큼의 것이 아니라 이 브라우저에 기억시킨다.
const ACCOUNT_KEY = 'hq_alimtalk_bill_account';

const HqAlimtalkBillsPage: React.FC = () => {
  const [month, setMonth] = useState(prevYearMonth());
  const [data, setData] = useState<AlimtalkBillList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // 청구 안내 발송 2단계 확인 — window.confirm 을 쓸 수 없어 버튼을 두 번 누르게 한다
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null);
  const [account, setAccount] = useState(() => localStorage.getItem(ACCOUNT_KEY) || '');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await hqAlimtalkBillService.list(month));
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? '알림톡 청구는 본부 대표만 볼 수 있습니다.'
          : e?.response?.data?.detail || '청구 내역을 불러오지 못했습니다.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const saveAccount = (v: string) => {
    setAccount(v);
    localStorage.setItem(ACCOUNT_KEY, v);
  };

  const generate = async () => {
    setBusy('generate');
    try {
      await hqAlimtalkBillService.generate(month);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || '청구서 생성에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  const togglePaid = async (b: AlimtalkBill) => {
    setBusy(b.organization_id);
    try {
      if (b.status === 'paid') {
        await hqAlimtalkBillService.markUnpaid(b.organization_id, b.year_month);
      } else {
        await hqAlimtalkBillService.markPaid(b.organization_id, b.year_month);
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || '입금 처리에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * 청구 안내 보내기 — 매장 문의창에 안내문을 남기고 '입금 대기'로 바꾼다.
   * 🔴 메일을 안 보는 원장도 화면에서 청구를 알 수 있게 하려고 만든 것이다(2026-09-09).
   *    한 번 보내면 매장 화면에 그대로 남으므로 확인 후 보낸다.
   */
  const sendNotice = async (b: AlimtalkBill) => {
    // 🔴 window.confirm 을 쓰지 않는다 — 환경에 따라 차단되어 "눌러도 반응 없음"으로
    //    보인다(저장소 규칙, 2026-07-29 실제 제보). 대신 두 번 누르게 한다.
    if (confirmTarget !== b.organization_id) {
      setConfirmTarget(b.organization_id);
      return;
    }
    setConfirmTarget(null);
    setBusy(b.organization_id);
    try {
      const res = await hqAlimtalkBillService.notify(b.organization_id, b.year_month);
      if (res?.payment_account_missing) {
        // 계좌가 서버에 없으면 안내문에 계좌가 안 들어간다 — 조용히 넘어가면 안 된다.
        setError(
          '안내는 보냈지만 서버에 입금 계좌가 설정돼 있지 않아 계좌가 빠졌습니다. ' +
            '서버 설정(BILLING_BANK_ACCOUNT)을 확인해 주세요.',
        );
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || '청구 안내 발송에 실패했습니다.');
    } finally {
      setBusy(null);
    }
  };

  const noticeText = (b: AlimtalkBill) => {
    const [y, m] = b.year_month.split('-');
    const lines = [
      `[${b.organization_name}] ${Number(m)}월 알림톡 사용료 안내`,
      '',
      `· 사용 기간: ${y}년 ${Number(m)}월 1일 ~ 말일`,
      `· 발송 건수: ${cnt(b.sent_count)}건`,
    ];
    if (b.free_quota > 0) {
      lines.push(`· 무료 제공: ${cnt(b.free_quota)}건`);
      lines.push(`· 청구 건수: ${cnt(b.billable_count)}건`);
    }
    lines.push(`· 단가: 건당 ${b.unit_price}원`);
    lines.push('');
    lines.push(`▶ 청구 금액: ${won(b.amount)}`);
    const types = Object.entries(b.by_type || {}).sort((a, c) => c[1] - a[1]);
    if (types.length) {
      lines.push('');
      lines.push('[내역]');
      types.forEach(([t, c]) => lines.push(`· ${typeLabel(t)} ${cnt(c)}건`));
    }
    if (account.trim()) {
      lines.push('');
      lines.push(`입금 계좌: ${account.trim()}`);
    }
    return lines.join('\n');
  };

  const copyNotice = async (b: AlimtalkBill) => {
    try {
      await navigator.clipboard.writeText(noticeText(b));
      setCopied(b.organization_id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError('복사에 실패했습니다. 안내문을 직접 선택해 복사해 주세요.');
    }
  };

  const bills = data?.bills ?? [];
  const paidCount = useMemo(() => bills.filter((b) => b.status === 'paid').length, [bills]);

  return (
    <div className="sm:p-6 max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">💬 알림톡 사용료 청구</h1>
        <input
          type="month"
          value={month}
          max={thisYearMonth()}
          onChange={(e) => setMonth(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
        />
      </div>
      <p className="text-xs text-slate-500 mb-5">
        매월 1일 오전 9시 10분에 지난달 청구서가 자동으로 만들어집니다. 발송 성공 건만 셉니다(실패 건 제외).
      </p>

      {loading && <div className="text-slate-500 py-10 text-center">불러오는 중…</div>}
      {error && (
        <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
      )}

      {!loading && data && (
        <>
          {!data.saved && bills.length > 0 && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-amber-800">
                아직 <b>청구서가 만들어지지 않은 달</b>입니다. 아래 숫자는 지금 발송 기록을 센 미리보기예요.
              </div>
              <button
                onClick={generate}
                disabled={busy === 'generate'}
                className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {busy === 'generate' ? '생성 중…' : '청구서 만들기'}
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card label={`${month} 총 발송`} value={cnt(data.total_sent) + '건'} />
            <Card label="총 청구 금액" value={won(data.total_amount)} highlight />
            <Card label="미입금" value={won(data.unpaid_amount)} danger={data.unpaid_amount > 0} />
            <Card label="입금 완료" value={`${paidCount} / ${bills.length}곳`} />
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <label className="text-xs text-slate-500 shrink-0">안내문에 넣을 입금 계좌</label>
            <input
              value={account}
              onChange={(e) => saveAccount(e.target.value)}
              placeholder="예: 국민은행 123456-78-901234 (주)엔티디"
              className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
            />
          </div>

          {bills.length === 0 ? (
            <div className="text-slate-500 py-10 text-center">이 달에 발송된 알림톡이 없습니다.</div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">매장 ({bills.length})</th>
                    <th className="text-right px-4 py-2 font-medium">발송</th>
                    <th className="text-right px-4 py-2 font-medium">청구 건수</th>
                    <th className="text-right px-4 py-2 font-medium">금액</th>
                    <th className="text-center px-4 py-2 font-medium">입금</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {bills.map((b) => {
                    const open = expanded === b.organization_id;
                    const paid = b.status === 'paid';
                    return (
                      <React.Fragment key={b.organization_id}>
                        <tr className="border-t border-slate-100 hover:bg-slate-50">
                          <td
                            className="px-4 py-3 cursor-pointer"
                            onClick={() => setExpanded(open ? null : b.organization_id)}
                          >
                            <span className="text-slate-400 mr-1">{open ? '▾' : '▸'}</span>
                            <span className="font-medium text-slate-800">{b.organization_name}</span>
                            {b.failed_count > 0 && (
                              <span className="ml-2 text-[11px] text-slate-400">
                                실패 {cnt(b.failed_count)}건 (청구 제외)
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-700 whitespace-nowrap">
                            {cnt(b.sent_count)}건
                          </td>
                          <td className="px-4 py-3 text-right text-slate-500 whitespace-nowrap">
                            {cnt(b.billable_count)}건
                            <div className="text-[11px] text-slate-400">
                              건당 {b.unit_price}원
                              {b.free_quota > 0 && ` · 무료 ${cnt(b.free_quota)}`}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-800 whitespace-nowrap">
                            {won(b.amount)}
                          </td>
                          <td className="px-4 py-3 text-center whitespace-nowrap">
                            {data.saved ? (
                              <button
                                onClick={() => togglePaid(b)}
                                disabled={busy === b.organization_id}
                                className={`text-xs rounded-lg px-3 py-1.5 border disabled:opacity-50 ${
                                  paid
                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                                }`}
                              >
                                {paid ? '✓ 입금 완료' : '입금 확인'}
                              </button>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-3">
                              {data.saved && !paid && (
                                <button
                                  onClick={() => sendNotice(b)}
                                  disabled={busy === b.organization_id}
                                  className={`text-xs rounded-lg px-3 py-1.5 border disabled:opacity-50 ${
                                    confirmTarget === b.organization_id
                                      ? 'border-amber-400 bg-amber-50 text-amber-800'
                                      : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                                  }`}
                                  title="매장 문의창에 청구 안내를 남기고 '입금 대기'로 바꿉니다"
                                >
                                  {confirmTarget === b.organization_id
                                    ? '한 번 더 누르면 보냅니다'
                                    : b.status === 'billed'
                                      ? '안내 다시 보내기'
                                      : '청구 안내 보내기'}
                                </button>
                              )}
                              <button
                                onClick={() => copyNotice(b)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                {copied === b.organization_id ? '복사됨!' : '안내문 복사'}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={6} className="px-4 py-3">
                              <div className="text-xs text-slate-500 mb-2">유형별 발송 내역</div>
                              <div className="flex flex-wrap gap-2 mb-3">
                                {Object.entries(b.by_type || {})
                                  .sort((x, y) => y[1] - x[1])
                                  .map(([t, c]) => (
                                    <span
                                      key={t}
                                      className="bg-white border border-slate-200 rounded-lg px-3 py-1 text-xs text-slate-700"
                                    >
                                      {typeLabel(t)} <b>{cnt(c)}</b>건
                                    </span>
                                  ))}
                              </div>
                              <div className="text-xs text-slate-500 mb-1">매장에 보낼 안내문</div>
                              <pre className="bg-white border border-slate-200 rounded-lg p-3 text-xs text-slate-700 whitespace-pre-wrap">
                                {noticeText(b)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                    <td className="px-4 py-3 text-slate-700">합계</td>
                    <td className="px-4 py-3 text-right">{cnt(data.total_sent)}건</td>
                    <td />
                    <td className="px-4 py-3 text-right">{won(data.total_amount)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <p className="text-[11px] text-slate-400 mt-3">
            단가·무료 건수는 매장별로 다르게 줄 수 있습니다(기본 {data.unit_price_default}원 · 무료{' '}
            {cnt(data.free_quota_default)}건). 청구서를 만든 뒤에는 그때의 단가가 그대로 보존되어,
            나중에 단가를 바꿔도 지난 청구 금액은 변하지 않습니다.
          </p>
        </>
      )}
    </div>
  );
};

const Card: React.FC<{ label: string; value: string; highlight?: boolean; danger?: boolean }> = ({
  label,
  value,
  highlight,
  danger,
}) => (
  <div
    className={`rounded-xl px-4 py-3 border ${
      highlight
        ? 'bg-blue-50 border-blue-200'
        : danger
        ? 'bg-red-50 border-red-200'
        : 'bg-white border-slate-200'
    }`}
  >
    <div className="text-xs text-slate-500">{label}</div>
    <div
      className={`text-lg font-bold mt-0.5 truncate ${
        highlight ? 'text-blue-700' : danger ? 'text-red-600' : 'text-slate-800'
      }`}
      title={value}
    >
      {value}
    </div>
  </div>
);

export default HqAlimtalkBillsPage;
